import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import emailjs from '@emailjs/browser';
import DarkVeil from './micro/ColorBends';
import '../styles/contact.scss';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const contentRef = useRef(null);
  const modalRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  // EmailJS configuration
  const EMAILJS_SERVICE_ID = 'service_7me9g3n';
  const EMAILJS_TEMPLATE_ID = 'template_eiqpn1q';
  const EMAILJS_PUBLIC_KEY = 'X89WRP3eOxuLFUagi';

  // Refresh ScrollTrigger when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (contentRef.current) {
        gsap.fromTo(
          contentRef.current.querySelectorAll('.reveal'),
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.08,
            ease: 'power3.out',
            delay: 0.15
          }
        );
      }
    });

    return () => ctx.revert();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          service: formData.service,
          message: formData.message,
        },
        EMAILJS_PUBLIC_KEY
      );
      
      console.log('EmailJS Success:', response);
      
      setIsSubmitting(false);
      setShowModal(true);
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        service: '',
        message: ''
      });
    } catch (err) {
      console.error('EmailJS Error Details:', {
        text: err.text,
        status: err.status,
        message: err.message,
        fullError: err
      });
      setIsSubmitting(false);
      setError(
        err.text || 
        t('contact.error') || 
        'Failed to send message. Please try again.'
      );
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  // Animate modal when it opens and lock body scroll
  useEffect(() => {
    if (showModal) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      if (modalRef.current) {
        gsap.fromTo(
          modalRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.3, ease: 'power2.out' }
        );
        gsap.fromTo(
          modalRef.current.querySelector('.contact-modal__content'),
          { opacity: 0, scale: 0.9, y: 20 },
          { opacity: 1, scale: 1, y: 0, duration: 0.4, ease: 'back.out(1.7)', delay: 0.1 }
        );
      }
    } else {
      // Unlock body scroll
      document.body.style.overflow = '';
    }

    // Cleanup: unlock scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  return (
    <section className="contact">
      <div className="contact__bg">
        <DarkVeil
          hueShift={0}
          noiseIntensity={0.02}
          scanlineIntensity={0}
          speed={0.25}
          warpAmount={0.2}
          resolutionScale={0.6}
        />
        <div className="contact__overlay" />
      </div>

      <div className="contact__container" ref={contentRef}>
        <div className="contact__header reveal">
          <span className="contact__label">{t('contact.label')}</span>
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.subtitle')}</p>
        </div>

        <form className="contact__form" onSubmit={handleSubmit}>
          <div className="contact__fields">
            <div className="contact__field reveal">
              <label htmlFor="name">{t('contact.name')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="contact__field reveal">
              <label htmlFor="email">{t('contact.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="contact__field reveal">
              <label htmlFor="subject">{t('contact.subject')}</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>

            <div className="contact__field reveal">
              <label htmlFor="service">{t('contact.service')}</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
              >
                <option value="">{t('contact.service_select')}</option>
                <option value="website">{t('contact.service_website')}</option>
                <option value="webapp">{t('contact.service_webapp')}</option>
                <option value="ecommerce">{t('contact.service_ecommerce')}</option>
                <option value="redesign">{t('contact.service_redesign')}</option>
                <option value="other">{t('contact.service_other')}</option>
              </select>
            </div>

            <div className="contact__field contact__field--full reveal">
              <label htmlFor="message">{t('contact.message')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                required
              />
            </div>
          </div>

          {error && (
            <div className="contact__error reveal">
              {error}
            </div>
          )}

          <button type="submit" className="contact__btn reveal" disabled={isSubmitting}>
            {isSubmitting ? t('contact.submitting') : t('contact.submit')}
            {!isSubmitting && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </form>

        {/* Success Modal - Rendered via Portal to cover entire page */}
        {showModal && createPortal(
          <div className="contact-modal" ref={modalRef} onClick={closeModal}>
            <div className="contact-modal__content" onClick={(e) => e.stopPropagation()}>
              <button className="contact-modal__close" onClick={closeModal} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <div className="contact-modal__icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              <h2>{t('contact.success_title')}</h2>
              <p>{t('contact.success_desc')}</p>
              
              <button className="contact-modal__btn" onClick={closeModal}>
                {t('contact.modal_close')}
              </button>
            </div>
          </div>,
          document.body
        )}

        <div className="contact__footer reveal">
          <a href="mailto:info@yveswebdesign.com" className="contact__link">
            info@yveswebdesign.com
          </a>
          <span className="contact__divider">·</span>
          <span className="contact__response">{t('contact.response')}</span>
        </div>
      </div>
    </section>
  );
};

export default Contact;
